// Phase 44.31.7 — Pagina dedicata DOCUMENTI driver, separata da /driver/vehicle.
// Estrae la section che prima viveva dentro Vehicle.jsx.
// Logica copiata 1:1 (stessi endpoint /driver/documents, stessa UX upload).
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, CheckCircle2, X, Loader2, Upload, Eye, KeyRound, Save,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const DOC_STATUS_LABEL = {
  missing: { label: "Documenti mancanti", tone: "red" },
  incomplete: { label: "Documenti incompleti", tone: "amber" },
  pending: { label: "In verifica", tone: "amber" },
  verified_basic: { label: "Verificato base", tone: "emerald" },
  verified_full: { label: "Verificato completo", tone: "emerald" },
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function DriverDocuments() {
  const { user, refresh } = useAuth();
  const [docState, setDocState] = useState({
    types: [],
    documents: {},
    status: "missing",
    missing_required: [],
    max_bytes: 6 * 1024 * 1024,
  });
  const [docsLoading, setDocsLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const fileRefs = useRef({});

  // Phase 44.31.11 — Numero patente + scadenza gestiti qui (patente è un documento).
  const driverInfo = user?.driver_info || {};
  const [licenseNumber, setLicenseNumber] = useState(driverInfo.license_number || "");
  const [licenseExpiry, setLicenseExpiry] = useState(driverInfo.license_expiry || "");
  const [savingLicense, setSavingLicense] = useState(false);

  useEffect(() => {
    setLicenseNumber(user?.driver_info?.license_number || "");
    setLicenseExpiry(user?.driver_info?.license_expiry || "");
  }, [user]);

  const saveLicenseData = async () => {
    setSavingLicense(true);
    try {
      await api.put("/driver/vehicle", {
        license_number: licenseNumber || null,
        license_expiry: licenseExpiry || null,
      });
      toast.success("Dati patente salvati");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingLicense(false);
    }
  };

  const loadDocs = async () => {
    setDocsLoading(true);
    try {
      const { data } = await api.get("/driver/documents");
      setDocState(data);
    } catch { /* silent */ }
    finally { setDocsLoading(false); }
  };

  useEffect(() => { loadDocs(); }, []);

  const docTypes = useMemo(() => docState.types || [], [docState.types]);
  const statusInfo = DOC_STATUS_LABEL[docState.status] || DOC_STATUS_LABEL.missing;

  const onPickFile = (type) => () => { fileRefs.current[type]?.click(); };

  const onFileChange = (type) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > docState.max_bytes) {
      toast.error(`File troppo grande (max ${(docState.max_bytes / (1024 * 1024)).toFixed(0)} MB)`);
      return;
    }
    setUploading((u) => ({ ...u, [type]: true }));
    try {
      const data_base64 = await fileToBase64(file);
      await api.post("/driver/documents", {
        type, filename: file.name, mime: file.type || "application/octet-stream", data_base64,
      });
      toast.success("Documento caricato");
      await loadDocs();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading((u) => ({ ...u, [type]: false }));
    }
  };

  const removeDoc = async (type) => {
    if (!window.confirm("Eliminare il documento caricato?")) return;
    try {
      await api.delete(`/driver/documents/${type}`);
      toast.success("Documento rimosso");
      await loadDocs();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const viewDoc = async (type) => {
    try {
      const { data } = await api.get(`/driver/documents/${type}/file`);
      const b64 = data.data_base64;
      const url = b64.startsWith("data:") ? b64 : `data:${data.mime};base64,${b64}`;
      const w = window.open();
      if (!w) {
        toast.error("Disabilita il blocco popup per visualizzare il documento");
        return;
      }
      w.document.write(
        data.mime?.startsWith("image/")
          ? `<body style="margin:0;background:#0a0a0a"><img src="${url}" style="max-width:100%;height:auto;display:block;margin:auto"/></body>`
          : `<body style="margin:0"><iframe src="${url}" style="border:0;width:100%;height:100vh"></iframe></body>`
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="driver-documents-page">
      <Link to="/driver/profile" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Profilo
      </Link>
      <div>
        <p className="overline">Driver · Documenti</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Verifica documenti</h1>
        <p className="text-dapnow-muted text-sm mt-2">
          Carica i documenti richiesti (immagine o PDF, max {(docState.max_bytes / (1024 * 1024)).toFixed(0)} MB).
          Il casellario giudiziale è opzionale e sblocca il badge <em>Driver verificato completo</em>.
        </p>
      </div>

      <section className="card-soft p-5 sm:p-6 space-y-3" data-testid="documents-license-block">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-dapnow-blue" />
          <p className="overline">Patente di guida</p>
        </div>
        <p className="text-xs text-dapnow-muted">
          Numero patente + data scadenza. Riceverai un avviso 30 giorni prima della scadenza.
          Carica anche la <strong>foto della patente</strong> nella sezione sotto.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 min-w-0">
          <div className="min-w-0">
            <label className="text-[10px] font-bold uppercase tracking-wider text-dapnow-muted">Numero patente</label>
            <input
              type="text"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              placeholder="Es. CH 12 3456 78"
              data-testid="documents-license-number"
              className="input-tall mt-1 w-full"
            />
          </div>
          <div className="min-w-0">
            <label className="text-[10px] font-bold uppercase tracking-wider text-dapnow-muted">Scadenza patente</label>
            <input
              type="date"
              value={licenseExpiry}
              onChange={(e) => setLicenseExpiry(e.target.value)}
              data-testid="documents-license-expiry"
              className="input-tall mt-1 w-full"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={saveLicenseData}
            disabled={savingLicense}
            data-testid="documents-license-save"
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-xl bg-dapnow-blue text-white text-sm font-bold disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" /> {savingLicense ? "Salvo…" : "Salva dati patente"}
          </button>
        </div>
      </section>

      <section className="card-soft p-5 sm:p-6 space-y-4" data-testid="documents-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="overline">Stato verifica</p>
            <h2 className="font-display text-lg font-bold tracking-tight mt-1">
              {statusInfo.label === "Verificato completo" ? "Tutto a posto" : "Documenti richiesti"}
            </h2>
          </div>
          <span
            data-testid="documents-status-pill"
            className={`pill ${
              statusInfo.tone === "emerald" ? "bg-emerald-50 text-emerald-700"
                : statusInfo.tone === "amber" ? "bg-amber-50 text-amber-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {statusInfo.label}
          </span>
        </div>

        {docsLoading ? (
          <div className="flex items-center gap-2 text-sm text-dapnow-muted py-4"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento documenti…</div>
        ) : (
          <div className="space-y-3" data-testid="documents-list">
            {docTypes.map((t) => {
              const meta = docState.documents?.[t.id];
              const isUploading = uploading[t.id];
              const hasDoc = Boolean(meta?.uploaded_at);
              const status = meta?.status || (hasDoc ? "pending" : "missing");
              const isVerified = status === "verified";
              const isRejected = status === "rejected";
              return (
                <div
                  key={t.id}
                  data-testid={`doc-row-${t.id}`}
                  className={`flex items-start gap-3 rounded-2xl border p-3 sm:p-4 ${
                    isVerified ? "border-emerald-200 bg-emerald-50/40"
                      : isRejected ? "border-red-200 bg-red-50/40"
                      : hasDoc ? "border-amber-200 bg-amber-50/30"
                      : "border-dapnow-line bg-white"
                  }`}
                >
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${
                    isVerified ? "bg-emerald-100 text-emerald-700"
                      : isRejected ? "bg-red-100 text-red-700"
                      : "bg-dapnow-bg text-dapnow-ink"
                  }`}>
                    {isVerified ? <CheckCircle2 className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm font-semibold tracking-tight">
                      {t.label} {t.required ? <span className="text-dapnow-red">*</span> : <span className="text-xs font-medium text-dapnow-muted">(opzionale)</span>}
                    </p>
                    {hasDoc ? (
                      <p className="text-xs text-dapnow-muted truncate">
                        {meta.filename} · {((meta.size_bytes || 0) / 1024).toFixed(0)} KB
                      </p>
                    ) : (
                      <p className="text-xs text-dapnow-muted">Nessun file caricato</p>
                    )}
                    {isVerified && (
                      <p className="text-xs text-emerald-700 mt-0.5">Approvato dall'admin</p>
                    )}
                    {isRejected && meta?.rejected_reason && (
                      <p className="text-xs text-dapnow-red mt-1 font-medium" data-testid={`doc-rejected-reason-${t.id}`}>
                        Rifiutato: {meta.rejected_reason}
                      </p>
                    )}
                    {!isVerified && !isRejected && hasDoc && (
                      <p className="text-xs text-amber-700 mt-0.5">In verifica admin…</p>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    ref={(el) => { fileRefs.current[t.id] = el; }}
                    onChange={onFileChange(t.id)}
                    data-testid={`doc-file-${t.id}`}
                    className="hidden"
                  />
                  <div className="flex items-center gap-1.5 self-center shrink-0">
                    {hasDoc && (
                      <button
                        type="button"
                        onClick={() => viewDoc(t.id)}
                        data-testid={`doc-view-${t.id}`}
                        className="hidden sm:inline-flex items-center gap-1 h-9 px-3 rounded-xl border border-dapnow-line bg-white text-sm font-semibold hover:bg-dapnow-bg transition"
                      >
                        <Eye className="h-4 w-4" /> Vedi
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onPickFile(t.id)}
                      disabled={isUploading}
                      data-testid={`doc-upload-${t.id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-xl bg-dapnow-blue text-white text-sm font-semibold hover:bg-dapnow-blue-hover transition disabled:opacity-50"
                    >
                      {isUploading ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Carico…</>
                      ) : (
                        <><Upload className="h-4 w-4" /> {hasDoc ? "Sostituisci" : "Carica"}</>
                      )}
                    </button>
                    {hasDoc && (
                      <button
                        type="button"
                        onClick={() => removeDoc(t.id)}
                        data-testid={`doc-delete-${t.id}`}
                        aria-label="Elimina"
                        className="inline-flex items-center justify-center h-9 w-9 rounded-xl border border-dapnow-line bg-white text-dapnow-red hover:bg-red-50 transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!docsLoading && docState.missing_required?.length > 0 && (
          <p className="text-xs text-amber-700 font-medium" data-testid="documents-missing-hint">
            Per diventare operativo carica: {docState.missing_required.map((k) => docTypes.find((d) => d.id === k)?.label || k).join(", ")}.
          </p>
        )}
        {!docsLoading && docState.rejected_required?.length > 0 && (
          <p className="text-xs text-dapnow-red font-medium" data-testid="documents-rejected-hint">
            Documenti rifiutati dall'admin: {docState.rejected_required.map((k) => docTypes.find((d) => d.id === k)?.label || k).join(", ")}. Sostituisci il file per richiedere una nuova verifica.
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-dapnow-line bg-zinc-50/60 p-4 text-xs text-dapnow-muted">
        <p className="font-semibold text-dapnow-ink mb-1">Hai una scadenza patente o assicurazione?</p>
        Le scadenze documenti (patente, assicurazione veicolo) si gestiscono insieme al veicolo.{" "}
        <Link to="/driver/vehicle" className="text-dapnow-blue font-semibold hover:underline">
          Vai a Veicolo →
        </Link>
      </section>
    </div>
  );
}
